import { createSlice, type PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import type { IOffer, IOfferRow, IBuyerSelection, IOffersState, IFilterState } from '../types';
import { api } from '../api/client';

const initialFilters: IFilterState = {
    status_code: 'all',
    buyer_code: 'all',
    vendor_name: 'all',
    search: '',
    protection: false,
    my: false,
};

const initialState: IOffersState & { currentOffer: IOffer | null } = {
    items: [],
    selectedCode: null,
    currentOffer: null,
    rows: [],
    columns: [],
    buyers: [],
    selections: [],
    loading: false,
    loadingRows: false,
    loadingSelections: false,
    error: null,
    filters: initialFilters,
};

// ── Async thunks ──────────────────────────────────────────

export const fetchOffers = createAsyncThunk(
    'offers/fetchOffers',
    async (params: Record<string, string> | undefined, { rejectWithValue }) => {
        try {
            return await api.getOffers(params);
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

export const fetchOfferRows = createAsyncThunk(
    'offers/fetchOfferRows',
    async ({ code, params }: { code: number; params?: Record<string, string> }, { rejectWithValue }) => {
        try {
            return await api.getOfferRows(code, params);
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

export const fetchOfferColumns = createAsyncThunk(
    'offers/fetchOfferColumns',
    async (code: number, { rejectWithValue }) => {
        try {
            return await api.getOfferColumns(code);
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

export const hideOffer = createAsyncThunk(
    'offers/hideOffer',
    async (code: number, { rejectWithValue, dispatch }) => {
        try {
            await api.hideOffer(code);
            dispatch(fetchOffers(undefined));
            return code;
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

export const restoreOffer = createAsyncThunk(
    'offers/restoreOffer',
    async (code: number, { rejectWithValue, dispatch }) => {
        try {
            await api.restoreOffer(code);
            dispatch(fetchOffers(undefined));
            return code;
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

export const createOffer = createAsyncThunk(
    'offers/createOffer',
    async (data: Record<string, any>, { rejectWithValue }) => {
        try {
            return await api.createOffer(data);
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

export const updateOffer = createAsyncThunk(
    'offers/updateOffer',
    async ({ code, data }: { code: number; data: Partial<IOffer> }, { rejectWithValue }) => {
        try {
            return await api.updateOffer(code, data);
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

export const fetchOffer = createAsyncThunk(
    'offers/fetchOffer',
    async (code: number, { rejectWithValue }) => {
        try {
            return await api.getOffer(code);
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

export const saveOfferRows = createAsyncThunk(
    'offers/saveOfferRows',
    async ({ code, columns, rows }: { code: number; columns: any[]; rows: any[] }, { rejectWithValue }) => {
        try {
            return await api.saveOfferRows(code, { columns, rows });
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

export const coordinatorFinish = createAsyncThunk(
    'offers/coordinatorFinish',
    async (code: number, { rejectWithValue }) => {
        try {
            return await api.coordinatorFinish(code);
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

export const changeRowWareGroup = createAsyncThunk(
    'offers/changeRowWareGroup',
    async ({ code, rowCode, wareGroupCode }: { code: number; rowCode: number; wareGroupCode: number }, { rejectWithValue }) => {
        try {
            return await api.changeWareGroup(code, rowCode, wareGroupCode);
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

export const fetchOfferBuyers = createAsyncThunk(
    'offers/fetchOfferBuyers',
    async ({ code, params }: { code: number; params?: Record<string, string> }, { rejectWithValue }) => {
        try {
            return await api.getOfferBuyers(code, params);
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

export const fetchBuyerSelections = createAsyncThunk(
    'offers/fetchBuyerSelections',
    async ({ code, buyerCode, buyerCodes, params }: { code: number; buyerCode?: number; buyerCodes?: number[]; params?: Record<string, string> }, { rejectWithValue }) => {
        try {
            const codes = buyerCodes ?? (buyerCode ? [buyerCode] : []);
            const results = await Promise.all(
                codes.map(bc => api.getBuyerSelections(code, bc, params))
            );
            return results.flat();
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

export const saveBuyerSelections = createAsyncThunk(
    'offers/saveBuyerSelections',
    async ({ code, buyerCode, rows }: { code: number; buyerCode: number; rows: any[] }, { rejectWithValue }) => {
        try {
            return await api.saveBuyerSelections(code, buyerCode, rows);
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

export const finishBuyer = createAsyncThunk(
    'offers/finishBuyer',
    async ({ code, buyerCode }: { code: number; buyerCode: number }, { rejectWithValue }) => {
        try {
            return await api.finishBuyer(code, buyerCode);
        } catch (e: any) {
            return rejectWithValue(e.message);
        }
    }
);

// ── Slice ─────────────────────────────────────────────────

const offersSlice = createSlice({
    name: 'offers',
    initialState,
    reducers: {
        setSelectedCode: (state, action: PayloadAction<number | null>) => {
            state.selectedCode = action.payload;
            state.currentOffer = null;
            // Clear sub-data when deselecting
            if (action.payload === null) {
                state.rows = [];
                state.columns = [];
                state.buyers = [];
                state.selections = [];
            }
        },
        setFilter: (state, action: PayloadAction<Partial<IFilterState>>) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        resetFilters: (state) => {
            state.filters = initialFilters;
        },
        clearError: (state) => {
            state.error = null;
        },
        setError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
        },
        // Realtime: merge updated offer from WebSocket
        mergeOffer: (state, action: PayloadAction<IOffer>) => {
            const idx = state.items.findIndex(o => Number(o.code) === Number(action.payload.code));
            if (idx >= 0) {
                state.items[idx] = action.payload;
            } else {
                state.items.push(action.payload);
            }
        },
        // Realtime: merge updated rows from WebSocket
        mergeRows: (state, action: PayloadAction<IOfferRow[]>) => {
            for (const updated of action.payload) {
                const idx = state.rows.findIndex(r => r.code === updated.code);
                if (idx >= 0) {
                    state.rows[idx] = updated;
                } else {
                    state.rows.push(updated);
                }
            }
        },
        // Realtime: replace all rows (after bulk load)
        setRows: (state, action: PayloadAction<IOfferRow[]>) => {
            state.rows = action.payload;
        },
        // Realtime: merge buyer selections
        mergeSelections: (state, action: PayloadAction<IBuyerSelection[]>) => {
            state.selections = action.payload;
        },
    },
    extraReducers: (builder) => {
        // fetchOffers
        builder
            .addCase(fetchOffers.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(fetchOffers.fulfilled, (state, action) => { state.loading = false; state.items = action.payload; })
            .addCase(fetchOffers.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });

        // fetchOfferRows
        builder
            .addCase(fetchOfferRows.pending, (state) => { state.loadingRows = true; })
            .addCase(fetchOfferRows.fulfilled, (state, action) => { state.loadingRows = false; state.rows = action.payload; })
            .addCase(fetchOfferRows.rejected, (state, action) => { state.loadingRows = false; state.error = action.payload as string; });

        // fetchOfferColumns
        builder
            .addCase(fetchOfferColumns.fulfilled, (state, action) => { state.columns = action.payload; });

        // createOffer
        builder
            .addCase(createOffer.pending, (state) => { state.loading = true; })
            .addCase(createOffer.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) state.items.unshift(action.payload);
            })
            .addCase(createOffer.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });

        // updateOffer
        builder
            .addCase(updateOffer.pending, (state) => { state.loading = true; })
            .addCase(updateOffer.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    const idx = state.items.findIndex(o => Number(o.code) === Number(action.payload.code));
                    if (idx >= 0) state.items[idx] = action.payload;
                }
            })
            .addCase(updateOffer.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });

        // fetchOffer (single, for hidden offers)
        builder
            .addCase(fetchOffer.fulfilled, (state, action) => {
                if (action.payload) state.currentOffer = action.payload;
            });

        // saveOfferRows
        builder
            .addCase(saveOfferRows.pending, (state) => { state.loadingRows = true; })
            .addCase(saveOfferRows.fulfilled, (state, action) => { state.loadingRows = false; state.rows = action.payload; })
            .addCase(saveOfferRows.rejected, (state, action) => { state.loadingRows = false; state.error = action.payload as string; });

        // fetchOfferBuyers
        builder
            .addCase(fetchOfferBuyers.pending, (state) => { state.loading = true; })
            .addCase(fetchOfferBuyers.fulfilled, (state, action) => { state.loading = false; state.buyers = action.payload; })
            .addCase(fetchOfferBuyers.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });

        // fetchBuyerSelections
        builder
            .addCase(fetchBuyerSelections.pending, (state) => { state.loadingSelections = true; })
            .addCase(fetchBuyerSelections.fulfilled, (state, action) => { state.loadingSelections = false; state.selections = action.payload; })
            .addCase(fetchBuyerSelections.rejected, (state, action) => { state.loadingSelections = false; state.error = action.payload as string; });

        // saveBuyerSelections
        builder
            .addCase(saveBuyerSelections.pending, (state) => { state.loadingSelections = true; })
            .addCase(saveBuyerSelections.fulfilled, (state, action) => { state.loadingSelections = false; state.selections = action.payload; })
            .addCase(saveBuyerSelections.rejected, (state, action) => { state.loadingSelections = false; state.error = action.payload as string; });

        // coordinatorFinish, finishBuyer, changeRowWareGroup
        builder
            .addCase(coordinatorFinish.pending, (state) => { state.loading = true; })
            .addCase(coordinatorFinish.fulfilled, (state) => { state.loading = false; })
            .addCase(coordinatorFinish.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });

        builder
            .addCase(finishBuyer.pending, (state) => { state.loading = true; })
            .addCase(finishBuyer.fulfilled, (state) => { state.loading = false; })
            .addCase(finishBuyer.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });
    },
});

export const {
    setSelectedCode, setFilter, resetFilters, clearError, setError,
    mergeOffer, mergeRows, setRows, mergeSelections,
} = offersSlice.actions;

export default offersSlice.reducer;
